import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useEPIAssignments,
  getEPIStatusLabel,
  getEPIStatusColor,
  getEPICategoryLabel,
} from '@/hooks/useRPL';
import { EPIAssignmentDialog } from './EPIAssignmentDialog';
import { Search, Plus, HardHat, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface EPIListProps {
  employeeId?: string;
  showAssignButton?: boolean;
}

export function EPIList({ employeeId, showAssignButton = true }: EPIListProps) {
  const { data: assignments, isLoading } = useEPIAssignments({
    employeeId,
    activeOnly: true,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const filteredAssignments = assignments?.filter((a) => {
    const epiName = a.epi_inventory?.epi_type?.name?.toLowerCase() || '';
    const employeeName = (a as any).employee?.full_name?.toLowerCase() || '';
    const search = searchTerm.toLowerCase();
    return epiName.includes(search) || employeeName.includes(search);
  });

  const isExpiringSoon = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    const days = Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days <= 30 && days > 0;
  };

  const isExpired = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar EPIs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        {showAssignButton && (
          <Button onClick={() => setAssignDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Asignar EPI
          </Button>
        )}
      </div>

      {filteredAssignments?.length === 0 ? (
        <Card className="p-8 text-center">
          <HardHat className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No hay EPIs asignados</p>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredAssignments?.map((assignment) => {
            const epi = assignment.epi_inventory;
            const epiType = epi?.epi_type;
            const expiringSoon = isExpiringSoon(epi?.expiry_date || null);
            const expired = isExpired(epi?.expiry_date || null);

            return (
              <Card
                key={assignment.id}
                className={`${expired ? 'border-red-500' : expiringSoon ? 'border-yellow-500' : ''}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{epiType?.name}</CardTitle>
                    {epiType?.category && (
                      <Badge variant="outline">{getEPICategoryLabel(epiType.category)}</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {!employeeId && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Empleado:</span>
                      <span className="font-medium">{(assignment as any).employee?.full_name}</span>
                    </div>
                  )}

                  {epi?.serial_number && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Serie:</span>
                      <span>{epi.serial_number}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Asignado:</span>
                    <span>{format(new Date(assignment.assigned_at), 'dd/MM/yyyy', { locale: es })}</span>
                  </div>

                  {epi?.expiry_date && (
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-muted-foreground">Caducidad:</span>
                      <span className={`flex items-center gap-1 ${expired ? 'text-red-500' : expiringSoon ? 'text-yellow-500' : ''}`}>
                        {(expired || expiringSoon) && <AlertTriangle className="w-3 h-3" />}
                        {format(new Date(epi.expiry_date), 'dd/MM/yyyy', { locale: es })}
                      </span>
                    </div>
                  )}

                  <Badge className={getEPIStatusColor(epi?.status || 'assigned')}>
                    {getEPIStatusLabel(epi?.status || 'assigned')}
                  </Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <EPIAssignmentDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        preselectedEmployeeId={employeeId}
      />
    </div>
  );
}
